import { css } from '@styled-system/css';
import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';

import { AuthGuard } from '@/features/auth/components';
import { PageHeader } from '@/shared/components/PageHeader';
import { LegacyVersionBanner } from '@/shared/components/layout/LegacyVersionBanner';
import { MainSidebar } from '@/shared/components/layout/MainSidebar';
import { MobileBottomNav } from '@/shared/components/layout/MobileBottomNav';
import { MobileTopBar } from '@/shared/components/layout/MobileTopBar';

export interface AppLayoutProps {
  children: ReactNode;
  className?: string;
  title?: string;
  breadcrumbs?: Array<{ title: string; onClick?: () => void }>;
  /** Leading slot before the title (thumbnail, icon…) — desktop only. */
  headerLeading?: ReactNode;
  /** Inline extras on the title row (chips, external link…) — desktop only. */
  headerTitleExtras?: ReactNode;
  /** Right-aligned actions in the page header bar (desktop only). */
  headerActions?: ReactNode;
  /** Inline stats in the page header (desktop only). */
  headerStats?: ReactNode;
  /** Centered slot in the page header — e.g. global search (desktop only). */
  headerCenter?: ReactNode;
  /** Optional small mono eyebrow above the title (desktop only). */
  eyebrow?: string;
  contentPadding?: string;
  /** When true, the page header sticks to the top of the scrolling card. */
  stickyHeader?: boolean;
}

/**
 * App shell — desktop : sidebar à gauche + card avec header sticky.
 * Mobile : top bar EN FLUX en haut (pas en hover) + content scrollable +
 * pill flottante en bas (fixed, détachée des bords).
 */
export const AppLayout: FC<AppLayoutProps> = ({
  children,
  className,
  title,
  breadcrumbs,
  headerLeading,
  headerTitleExtras,
  headerActions,
  headerStats,
  headerCenter,
  eyebrow,
  contentPadding = '16px 24px 24px',
  stickyHeader = true,
}) => {
  useEffect(() => {
    document.title = title ?? 'Recop';
  }, [title]);

  // Le shell mobile est un flex column (top bar + main) — la top bar prend
  // sa hauteur naturelle, le main prend le reste. Sur desktop, le shell est
  // un flex row classique (sidebar + main).
  const shell = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100vw',
    bg: 'surface.canvas',
    color: 'text.primary',
    fontFamily: 'sans',
    overflow: 'hidden',
  });

  const root = css({
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: { base: 'column', md: 'row' },
    padding: { base: 0, md: '12px' },
    gap: 0,
    overflow: 'hidden',
  });

  // Spacer interne au body : l'espace pour la pill flottante est créé À
  // L'INTÉRIEUR de la card scrollable (pas dans le padding du root) pour
  // éviter d'exposer une bande de canvas grise en mode clair.
  const mobileBottomSpacer = css({
    display: { base: 'block', md: 'none' },
    height: 'calc(72px + env(safe-area-inset-bottom))',
    flexShrink: 0,
  });

  const sidebarSlot = css({
    flexShrink: 0,
    display: { base: 'none', md: 'flex' },
  });

  const card = css({
    flex: 1,
    minWidth: 0,
    bg: 'surface.default',
    color: 'text.primary',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: { base: 0, md: 'xl' },
    marginLeft: { base: 0, md: '12px' },
    overflow: 'hidden',
    boxShadow: { base: 'none', md: 'xs' },
    borderWidth: { base: 0, md: '1px' },
    borderStyle: 'solid',
    borderColor: 'border.default',
  });

  const headerRow = css({
    // Page header is desktop-only — on mobile, page-specific actions live in
    // an inline toolbar (per-page) and the global chrome is the top/bottom bars.
    display: { base: 'none', md: 'block' },
    flexShrink: 0,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'border.default',
    bg: 'surface.default',
    position: stickyHeader ? 'sticky' : 'static',
    top: 0,
    zIndex: 10,
  });

  const body = css({
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    bg: 'surface.default',
  });

  return (
    <AuthGuard>
      <div className={shell}>
        <LegacyVersionBanner />
        <MobileTopBar />

        <div className={`${root} ${className ?? ''}`}>
          <div className={sidebarSlot}>
            <MainSidebar />
          </div>

          <main className={card}>
            {(title || headerActions) && (
              <div className={headerRow}>
                <PageHeader
                  title={title ?? ''}
                  leading={headerLeading}
                  eyebrow={eyebrow}
                  breadcrumbs={breadcrumbs}
                  titleExtras={headerTitleExtras}
                  stats={headerStats}
                  center={headerCenter}
                  actions={headerActions}
                />
              </div>
            )}
            <div
              className={body}
              data-scroll-container=""
              style={{ padding: title || headerActions ? contentPadding : 0 }}
            >
              {children}
              <div className={mobileBottomSpacer} />
            </div>
          </main>
        </div>
      </div>

      <MobileBottomNav />
    </AuthGuard>
  );
};
